import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { formatDate } from "@/utils/formatDate";
import { addYears, startOfMonth, startOfToday } from "date-fns";
import { CalendarDays, X } from "lucide-react";
import { useState } from "react";

const FIELD_CLASS =
  "flex h-12 w-full items-center justify-between rounded-2xl border-0 bg-primary-soft px-4 py-3.5 text-sm font-semibold text-foreground hover:bg-primary-soft";

type DateSelectProps = {
  label: string;
  value?: Date;
  placeholder?: string;
  disabled?: {
    before?: Date;
    after?: Date;
  };
  onChange: (value: Date) => void;
};

export function DateSelect({
  label,
  value,
  placeholder = "Select date",
  disabled,
  onChange,
}: DateSelectProps) {
  const isMobile = useMobile();
  const [open, setOpen] = useState(false);

  const minDate = disabled?.before ?? startOfToday();
  const maxDate = disabled?.after ?? addYears(startOfToday(), 1);

  const minMonth = startOfMonth(minDate);
  const maxMonth = startOfMonth(maxDate);

  // Initialize the month state to the start of the month of the selected value or minDate
  const [month, setMonth] = useState(() => startOfMonth(value ?? minDate));

  // Determine if the previous and next month buttons should be disabled
  const isPrevDisabled = month <= minMonth;
  const isNextDisabled = month >= maxMonth;

  const handleSelect = (date: Date | undefined) => {
    if (!date) return;
    onChange(date);
    setMonth(startOfMonth(date));
    setOpen(false);
  };

  const trigger = (
    <Button type="button" className={FIELD_CLASS}>
      <span
        className={cn("text-sm font-medium", !value && "text-muted-foreground")}
      >
        {value ? formatDate(value) : placeholder}
      </span>
      <CalendarDays size={16} aria-hidden="true" />
    </Button>
  );

  const calendar = (
    <Calendar
      mode="single"
      selected={value}
      disabled={{
        before: minDate,
        after: maxDate,
      }}
      month={month}
      onMonthChange={setMonth}
      onSelect={handleSelect}
      className="w-full p-0 bg-transparent"
      classNames={{
        button_previous: cn(
          "bg-transparent border flex items-center justify-center border-border hover:bg-gray-100 h-10 w-10 md:h-8 md:w-8 rounded-full",
          isPrevDisabled && "opacity-50 pointer-events-none",
        ),
        button_next: cn(
          "bg-transparent border flex items-center justify-center border-border hover:bg-gray-100 h-10 w-10 md:h-8 md:w-8 rounded-full",
          isNextDisabled && "opacity-50 pointer-events-none",
        ),
      }}
    />
  );

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs font-bold text-field">{label}</Label>
      {isMobile ? (
        <Drawer open={open} onOpenChange={setOpen} showSwipeHandle>
          <DrawerTrigger render={trigger} />
          <DrawerContent>
            <DrawerClose className="p-4 border-b border-border">
              <Button
                variant="outline"
                className="z-50 rounded-full h-10 w-10"
                onClick={() => setOpen(false)}
              >
                <X size={24} className="text-muted shrink-0" />
              </Button>
            </DrawerClose>
            <div className="p-4">{calendar}</div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger render={trigger} />
          <PopoverContent
            align="start"
            className="w-auto rounded-2xl p-2 shadow-xl"
          >
            {calendar}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
